// The SubAgents tab of the side pane: the workers the open session
// delegated, the newest first, each with what it runs on and what it is
// doing. Those at work are listed under Live, and those that have ended
// under Completed, which is folded until he opens it. A row opens that
// worker here, in the tab, with the lead still in the centre: what it is
// and does, its transcript as it is read, a way back to the list and an icon
// that maximises it to its own page. One at work can be ended from either.
// Open on a worker, the tab says whose it is. Moved from today's Code tab
// (code/SubAgentsPane.tsx there): a worker is a session whose origin names
// its lead, from the store, and its transcript is followed as any session's.
// A worker is named by its lead's description of its task, else by its
// title, and shows its lead's verdict on its result once it has one
// (Poulpe-434). The server keeps no profile or allowance of a worker, nor
// anything of the agents an engine runs within its own session: those come
// from the open session's transcript, with controls only where their adapter
// grants them (Poulpe-433).
import { Fragment, useEffect, useId, useState } from 'react'
import { Link } from 'react-router-dom'
import type { TranscriptView } from '../sessions/follow'
import { EngineAgentRow, EngineAgentView, engineAgentsOf } from './EngineAgents'
import type { Me, Question, Runner, Session } from '../api/types'
import { ChevronRightIcon } from '../components/Icons'
import { useSessionPath } from '../projects/ProjectRoute'
import { useTranscript } from '../sessions/context'
import { Transcript } from '../transcript/Transcript'
import { ChevronDownIcon } from '../transcript/icons'
import { cx } from '../transcript/bits'
import { StateDot } from './bits'
import { agentLabel, isBusy, leadOf, shownState, stateText, verdictText, workersOf } from './format'
import { MaximiseIcon } from './icons'
import { useNoteTitle } from './titles'
import { SubAgentSettings, workerSettings } from './SubAgentSettings'

export interface SubAgentsPaneProps {
  /** The open session’s stream, shared with its centre transcript. */
  transcript?: TranscriptView
  /** The open session; none, when no session is open. */
  session: Session | null
  /** The board's sessions: the workers are found among them. */
  sessions: ReadonlyMap<string, Session>
  runners: readonly Runner[] | null
  /** A session's title as the page gives it (format.ts); '' when none is known. */
  titleOf: (session: Session) => string
  asked: ReadonlyMap<string, readonly Question[]>
  me: Me | null
  /** The workers he ended whose runner has not ended them yet. */
  ending: ReadonlySet<string>
  /** End a worker of the open session. */
  onEnd: (worker: Session) => void
  /** A worker of the open session to show open here: the one he minimised from its own page. */
  openSubagent?: string | null
  /** That one was taken, opened or not: the page asks for it no more. */
  onSubagentOpened?: () => void
}


/** End, for a worker that lives: beside its row, and over its transcript. */
function EndButton({ worker, name, ending, onEnd }: { worker: Session; name: string; ending: boolean; onEnd: (worker: Session) => void }) {
  if (worker.state === 'ended') return null
  return (
    <button type="button" className="button button-quiet button-small code-subagent-stop" aria-label={`End ${name}`} disabled={ending} onClick={() => onEnd(worker)}>
      {ending ? 'Ending…' : 'End'}
    </button>
  )
}

function SubAgentRow({
  worker,
  name,
  runners,
  asking,
  ending,
  focus,
  onOpen,
  onEnd,
}: {
  worker: Session
  name: string
  runners: readonly Runner[] | null
  asking: boolean
  ending: boolean
  /** He came back from this one: the keyboard is where it was. */
  focus: boolean
  onOpen: (id: string) => void
  onEnd: (worker: Session) => void
}) {
  const agent = agentLabel(worker, runners)
  // The lead's word on its result.
  const verdict = verdictText(worker.verdict)
  const parts = [
    <span key="engine" className={cx('code-subagent-engine', agent.id && `is-${agent.id}`)} title={worker.agent.vendor}>
      <span className="code-engine-dot" aria-hidden="true" />
      <span className="code-engine-name">{agent.name}</span>
    </span>,
    <SubAgentSettings key="settings" {...workerSettings(worker, runners)} />,
    <span key="status" className={cx('code-subagent-status', !ending && `is-${shownState(worker, asking)}`)}>
      {ending ? 'Ending…' : stateText(worker, asking)}
    </span>,
  ]
  return (
    <li className={cx('code-subagent', `is-${worker.state}`)}>
      {/* A button, not a link: it opens the worker here, and the address stays the lead's. */}
      <button type="button" className="code-subagent-open" title={name} autoFocus={focus} onClick={() => onOpen(worker.id)}>
        <StateDot session={worker} asking={asking} silent />
        {/* The spaces are for a screen reader: the boxes they stand between take none. */}
        <span className="code-subagent-text">
          <span className="code-subagent-title">{name}</span>{' '}
          <span className="code-subagent-meta">
            {parts.map((part, at) => (
              <Fragment key={at}>
                {at > 0 && ' · '}
                {part}
              </Fragment>
            ))}
          </span>
          {verdict && ' '}
          {verdict && (
            <span className="code-subagent-verdict" title={verdict.reason || undefined}>
              <span className={cx('code-subagent-verdict-label', `is-${verdict.value}`)}>{verdict.label}</span>
              {verdict.reason && ' '}
              {verdict.reason && <span className="code-subagent-verdict-reason">{verdict.reason}</span>}
            </span>
          )}
        </span>
      </button>
      <EndButton worker={worker} name={name} ending={ending} onEnd={onEnd} />
    </li>
  )
}

/**
 * A worker open in the tab: a way back to the list, what the worker is and
 * does as its row said it, then its transcript, to read only. Its prompt box
 * is on its own page: the icon maximises it to there.
 */
function SubAgentView({ worker, runners, name, asking, me, ending, onEnd, onBack }: { worker: Session; runners: readonly Runner[] | null; name: string; asking: boolean; me: Me | null; ending: boolean; onEnd: (worker: Session) => void; onBack: () => void }) {
  const view = useTranscript(worker)
  useNoteTitle(worker.id, view.info)
  const pathOf = useSessionPath()
  return (
    <section className="code-subagent-view" aria-label={name}>
      <header className="code-subagent-head">
        {/* The keyboard goes with him: the row it was on is gone. */}
        <button type="button" className="button button-small code-subagent-back" aria-label="Back to subagents" autoFocus onClick={onBack}>
          ‹ SubAgents
        </button>
        <span className="code-subagent-head-actions">
          <EndButton worker={worker} name={name} ending={ending} onEnd={onEnd} />
          <Link className="button button-quiet code-icon-button" to={pathOf(worker.id)} aria-label="Maximise" title="Maximise: its own page, with its prompt box">
            <MaximiseIcon size={14} />
          </Link>
        </span>
        <h2 className="code-subagent-name" title={name}>
          {name}
        </h2>
        <p className="code-subagent-state"><SubAgentSettings {...workerSettings(worker, runners)} /></p>
        <p className="code-subagent-state">
          <StateDot session={worker} asking={asking} silent />
          <span className={cx('code-subagent-status', !ending && `is-${shownState(worker, asking)}`)}>{ending ? 'Ending…' : stateText(worker, asking)}</span>
        </p>
      </header>
      <Transcript view={view} me={me} busy={isBusy(worker)} />
    </section>
  )
}

export function SubAgentsPane({ transcript, session, sessions, runners, titleOf, asked, me, ending, onEnd, openSubagent = null, onSubagentOpened }: SubAgentsPaneProps) {
  const completedId = useId()
  const pathOf = useSessionPath()
  const workers = workersOf(sessions.values(), session?.id)
  const agents = engineAgentsOf(transcript?.subagents ?? [])
  const entries = [
    ...workers.map(worker => ({kind: 'worker' as const, key: worker.id, worker, at: worker.created_at, live: worker.state !== 'ended'})),
    ...agents.map(agent => ({kind: 'engine' as const, key: `engine:${agent.id}`, agent, at: agent.at, live: agent.state === 'working' || agent.state === 'waiting'})),
  ].sort((a, b) => b.at.localeCompare(a.at))
  /** What a worker is called: its lead's description of its task, else what is known of its title. */
  const nameOf = (worker: Session) => worker.description || titleOf(worker) || 'Untitled subagent'
  // What is open here, and the row he came back from.
  const [opened, setOpened] = useState<string | null>(null)
  const [left, setLeft] = useState<string | null>(null)
  // Whether Completed is unfolded: kept here, so that he finds it as he left it when he comes back from a worker.
  const [unfolded, setUnfolded] = useState(false)
  const open = opened === null ? undefined : entries.find((entry) => entry.key === opened)
  // Another lead's workers are another list: none of them is open.
  useEffect(() => {
    setOpened(null)
    setLeft(null)
    setUnfolded(false)
  }, [session?.id])
  // The worker he minimised from its own page opens here: after the effect above, which would close it.
  // One the open session does not hold opens nothing.
  const asks = openSubagent !== null && workers.some((worker) => worker.id === openSubagent)
  useEffect(() => {
    if (openSubagent === null) return
    if (asks) {
      setLeft(null)
      setOpened(openSubagent)
      if (sessions.get(openSubagent)?.state === 'ended') setUnfolded(true)
    }
    // Taken once: the page asks for it no more, so that going back to the list is not undone.
    onSubagentOpened?.()
  }, [openSubagent, asks, sessions, onSubagentOpened])
  // A worker the page holds no more is open no more.
  useEffect(() => {
    if (opened !== null && !open) setOpened(null)
  }, [opened, open])
  // The row he came back to takes the keyboard once.
  useEffect(() => {
    if (left !== null && opened === null) setLeft(null)
  }, [left, opened])

  const lead = session ? leadOf(session) : null
  if (session && lead && entries.length === 0) {
    const leadSession = sessions.get(lead)
    return (
      <p className="code-pane-note">
        This is a subagent of <Link to={pathOf(lead)}>{(leadSession && titleOf(leadSession)) || 'Lead session'}</Link>.
      </p>
    )
  }
  if (!session || entries.length === 0) return <p className="code-pane-note muted">No subagents yet. A lead session starts them with its delegate tools.</p>
  if (open) {
    const back = () => {
      setLeft(open.key)
      // One that ended has its row in Completed: unfolded, so that the row is there to come back to.
      if (!open.live) setUnfolded(true)
      setOpened(null)
    }
    if (open.kind === 'engine') return <EngineAgentView key={open.key} session={session} runners={runners} agent={open.agent} transcript={transcript!} me={me} onBack={back} />
    return (
      <SubAgentView
        key={open.worker.id}
        worker={open.worker}
        runners={runners}
        name={nameOf(open.worker)}
        asking={asked.has(open.worker.id)}
        me={me}
        ending={ending.has(open.worker.id)}
        onEnd={onEnd}
        onBack={back}
      />
    )
  }
  const onOpen = (id: string) => {
    setLeft(null)
    setOpened(id)
  }
  // Those at work, then those that ended: each section the newest first, as the list is.
  const live = entries.filter(entry => entry.live)
  const completed = entries.filter(entry => !entry.live)
  const row = (entry: typeof entries[number]) => entry.kind === 'engine' ? (
    <EngineAgentRow key={entry.key} session={session} runners={runners} agent={entry.agent} focus={entry.key === left} onOpen={() => onOpen(entry.key)} />
  ) : workerRow(entry.worker)
  const workerRow = (worker: Session) => (
    <SubAgentRow
      key={worker.id}
      worker={worker}
      name={nameOf(worker)}
      runners={runners}
      asking={asked.has(worker.id)}
      ending={ending.has(worker.id)}
      focus={worker.id === left}
      onOpen={onOpen}
      onEnd={onEnd}
    />
  )
  return (
    <div className="code-subagents">
      {/* The spaces before the counts are for a screen reader: the boxes they stand between take none. */}
      <h2 className="code-subagents-title">
        Live <span className="code-subagents-count">{live.length}</span>
      </h2>
      {live.length > 0 ? (
        <ul className="code-subagents-list" aria-label="Live subagents">
          {live.map(row)}
        </ul>
      ) : (
        <p className="code-subagents-none muted">None at work.</p>
      )}
      {completed.length > 0 && (
        <>
          <button type="button" className="code-subagents-fold" aria-expanded={unfolded} aria-controls={completedId} onClick={() => setUnfolded((shown) => !shown)}>
            {unfolded ? <ChevronDownIcon size={12} /> : <ChevronRightIcon size={12} />}
            Completed <span className="code-subagents-count">{completed.length}</span>
          </button>
          {/* The list is always there for the button to name; its rows only while it is unfolded. */}
          <ul className="code-subagents-list" id={completedId} aria-label="Completed subagents" hidden={!unfolded}>
            {unfolded && completed.map(row)}
          </ul>
        </>
      )}
    </div>
  )
}
